import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { queryKeys } from '../lib/queryClient';
import * as detectionService from '../services/detectionService';
import * as dashboardService from '../services/dashboardService';
import type {
  DetectionQueryParams
} from '../types/api';

// Detections list query
export const useDetections = (params?: DetectionQueryParams) => {
  return useQuery({
    queryKey: queryKeys.detections.list(params),
    queryFn: () => detectionService.getDetections(params),
    select: (data) => {
      // API returns { detections: Detection[], pagination: Pagination }
      // Return consistent structure with detections array
      return {
        detections: data.detections || [],
        pagination: data.pagination || null
      };
    },
  });
};

// Single detection query
export const useDetection = (id: string, view?: string) => {
  return useQuery({
    queryKey: queryKeys.detections.detail(id),
    queryFn: () => detectionService.getDetection(id, view),
    select: (data) => data,
    enabled: !!id,
  });
};

// Detection stats query
export const useDetectionStats = (params?: {
  start_date?: string;
  end_date?: string;
  school_id?: number;
  camera_id?: number;
  student_id?: number;
}) => {
  return useQuery({
    queryKey: queryKeys.detections.stats(params),
    queryFn: () => detectionService.getDetectionStats(params),
    select: (data) => data,
  });
};

// Update detection mutation
export const useUpdateDetection = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: {
      id: string;
      data: {
        verification_status?: 'verified' | 'false_positive' | 'needs_review';
        notes?: string;
      };
    }) => detectionService.updateDetection(id, {
      verified: data.verification_status === 'verified',
      verification_notes: data.notes
    }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.detections.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.detections.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.detections.stats() });
      notifications.show({
        title: 'Success',
        message: 'Detection updated successfully',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to update detection',
        color: 'red',
      });
    },
  });
};

// Bulk update detections mutation
export const useBulkUpdateDetections = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      detection_ids: string[];
      verification_status: 'verified' | 'false_positive' | 'needs_review';
      notes?: string;
    }) => detectionService.bulkUpdateDetections(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.detections.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.detections.stats() });
      notifications.show({
        title: 'Success',
        message: `Detections updated successfully`,
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to update detections',
        color: 'red',
      });
    },
  });
};

// Verify detection mutation (alias for update)
export const useVerifyDetection = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, status, notes }: {
      id: string;
      status: 'verified' | 'rejected' | 'needs_review';
      notes?: string;
    }) => detectionService.updateDetection(id, {
      verified: status === 'verified',
      verification_notes: notes
    }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.detections.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.detections.detail(variables.id) });
      notifications.show({
        title: 'Success',
        message: 'Detection verified successfully',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to verify detection',
        color: 'red',
      });
    },
  });
};

// Update detection settings mutation
export const useUpdateDetectionSettings = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, settings }: {
      id: string;
      settings: Record<string, any>;
    }) => detectionService.updateDetection(id, settings),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.detections.detail(variables.id) });
      notifications.show({
        title: 'Success',
        message: 'Detection settings updated successfully',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to update detection settings',
        color: 'red',
      });
    },
  });
};

// Activity Feed hook that uses dashboard endpoint with student information
export const useActivityFeedDetections = (params?: DetectionQueryParams) => {
  return useQuery({
    queryKey: ['activity-feed-detections', params],
    queryFn: async () => {
      try {
        // Use dashboard activity feed endpoint which includes student information
        const response = await dashboardService.getActivityFeed({
          limit: params?.per_page || 25,
        });

        // Transform the response to match the expected detection format
        const activities = response.activities || [];
        const detections = activities.map((activity) => ({
          id: activity.id,
          type: 'detection',
          attributes: {
            id: activity.id,
            action_type: activity.metadata?.action || activity.title?.split(' - ')[1] || 'unknown',
            student_name: activity.actor?.name || 'Unknown Student',
            location: activity.related_entities?.find(e => e.type === 'area')?.name || activity.description?.match(/at (.+)$/)?.[1] || 'Unknown Location',
            camera_name: activity.related_entities?.find(e => e.type === 'camera')?.name || 'Unknown Camera',
            detected_at: activity.timestamp,
            confidence: activity.metadata?.confidence || 0.8,
            severity_level: activity.priority === 'high' ? 'high' : activity.priority === 'medium' ? 'medium' : 'low',
            formatted_action_type: activity.metadata?.action || activity.title?.split(' - ')[1] || 'Unknown Activity',
            formatted_confidence: `${Math.round((activity.metadata?.confidence || 0.8) * 100)}%`,
            detection_time: new Date(activity.timestamp).toLocaleString(),
            is_alert: activity.type === 'alert',
            area_name: activity.related_entities?.find(e => e.type === 'area')?.name || activity.description?.match(/at (.+)$/)?.[1] || 'Unknown Area',
            acknowledgment_status: {
              acknowledged: false,
              acknowledged_at: null,
              acknowledged_by: null
            },
            alert_info: null,
            status: 'processed',
            alert_status: 'active'
          },
          relationships: {
            camera: { data: null },
            area: { data: null },
            school: { data: null },
            acknowledged_by: { data: null },
            resolved_by: { data: null }
          }
        }));

        return {
          detections: detections,
          pagination: {
            current_page: params?.page || 1,
            total_pages: 1,
            total_count: detections.length,
            per_page: params?.per_page || 25,
            has_previous: false,
            has_next: false
          }
        };
      } catch (error) {
        console.error('Error fetching activity feed:', error);
        // Fallback to regular detections if dashboard endpoint fails
        return detectionService.getDetections(params);
      }
    },
    staleTime: 30000, // 30 seconds
  });
};
